import React, {FC, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {globalWindow} from '../../../../../libs/shared/helpers/src/browser/browserHelpers';
import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {textSelection} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useClipboard} from '../../../../helpers/hooks/clipboardHookHelpers';
import {conferenceTypeToText, isProviderConferenceType} from '../../../../helpers/models/conferenceHelpers';
import {
  ConferenceEntryPointModel,
  ConferenceEntryPointTypesEnum,
  ConferenceModel,
} from '../../../../models/api/conferenceModel';
import {LinkTrap} from '../../../common/linkTrap/linkTrap';
import {LinkTooltips} from '../../../common/tooltips/linkTooltips';

/*
 * Props.
 */

interface ConferenceTokenProps {
  conference: ConferenceModel;
  onRemoveClick?: Handler;
}

/*
 * Styles.
 */

const StyledButtonSectionDiv = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  grid-column-gap: 2px;
  visibility: hidden;
`;

const StyledWrapperDiv = styled.div`
  display: grid;
  grid-auto-columns: auto;
  grid-auto-flow: row;
  grid-row-gap: 12px;
  border: 1px solid ${(p) => p.theme.greys.shade40};
  border-radius: 4px;
  padding: 6px 8px;
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.shade70};
  word-break: break-all;
  position: relative;
  ${textSelection()}

  &:hover {
    ${StyledButtonSectionDiv} {
      visibility: visible;
    }
  }
`;

const StyledEntryPointsWrapperDiv = styled.div`
  display: grid;
  grid-auto-columns: auto;
  grid-auto-flow: row;
  grid-row-gap: 8px;
`;

const StyledTitleDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledSubtitleDiv = styled.div`
  font-weight: ${fontWeights.semibold};
`;

const StyledAnchor = styled.a`
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.interactiveText};
  cursor: pointer;

  &:hover {
    color: ${(p) => p.theme.interactiveTextHover};
  }
`;

const StyledIconButton = styled(IconButton)`
  padding: 3px;
`;

const StyledCheckmarkIcon = styled(Icon)`
  margin: 3px;
`;

/*
 * Component.
 */

export const ConferenceToken: FC<ConferenceTokenProps> = (props) => {
  const {name, type, entryPoints} = props.conference;

  const videoEntryPoint = entryPoints.find((p) => p.type === ConferenceEntryPointTypesEnum.VIDEO);
  const phoneEntryPoints = entryPoints.filter((p) => p.type === ConferenceEntryPointTypesEnum.PHONE);
  const sipEntryPoints = entryPoints.filter((p) => p.type === ConferenceEntryPointTypesEnum.SIP);
  const moreEntryPoint = entryPoints.find((p) => p.type === ConferenceEntryPointTypesEnum.MORE);

  const {copy} = useClipboard();
  const [shouldShowCopyCheckmark, setShouldShowCopyCheckmark] = useState(false);
  const onCopyClick = () => {
    if (!videoEntryPoint) {
      return;
    }

    copy(videoEntryPoint.uri);
    setShouldShowCopyCheckmark(true);
    globalWindow.setInstrumentedTimeout(() => {
      setShouldShowCopyCheckmark(false);
    }, 2000);
  };

  return (
    <StyledWrapperDiv>
      <div>
        <StyledTitleDiv>{name || conferenceTypeToText[type]}</StyledTitleDiv>
        {videoEntryPoint && renderEntryPoint(videoEntryPoint)}
      </div>
      {renderEntryPointsGroup(
        <Trans id="conference-token-phone-numbers">Phone numbers</Trans>,
        phoneEntryPoints,
      )}
      {renderEntryPointsGroup(<Trans id="conference-token-sip">SIP</Trans>, sipEntryPoints)}
      {moreEntryPoint && (
        <LinkTooltips>
          <LinkTrap>
            <StyledAnchor href={moreEntryPoint.uri} target="_blank" rel="noopener noreferrer">
              <Trans id="conference-entry-point-more">More dial-in options</Trans>
            </StyledAnchor>
          </LinkTrap>
        </LinkTooltips>
      )}
      {renderButtons(
        isProviderConferenceType(type) ? undefined : props.onRemoveClick,
        videoEntryPoint ? onCopyClick : undefined,
        shouldShowCopyCheckmark,
      )}
    </StyledWrapperDiv>
  );
};

function renderEntryPointsGroup(title: ReactNode, entryPoints: ReadonlyArray<ConferenceEntryPointModel>) {
  if (entryPoints.length === 0) {
    return null;
  }

  return (
    <div>
      <StyledSubtitleDiv>{title}</StyledSubtitleDiv>
      <StyledEntryPointsWrapperDiv>{entryPoints.map(renderEntryPoint)}</StyledEntryPointsWrapperDiv>
    </div>
  );
}

function renderEntryPoint(entryPoint: ConferenceEntryPointModel) {
  return (
    <div key={entryPoint.uri}>
      {renderAnchor(entryPoint)}
      {entryPoint.pin && (
        <div>
          <Trans id="conference-entry-point-pin">PIN:</Trans>
          {entryPoint.pin}
        </div>
      )}
      {entryPoint.accessCode && (
        <div>
          <Trans id="conference-entry-point-access-code">Access code:</Trans>
          {entryPoint.accessCode}
        </div>
      )}
      {entryPoint.meetingCode && (
        <div>
          <Trans id="conference-entry-point-meeting-code">Meeting code:</Trans>
          {entryPoint.meetingCode}
        </div>
      )}
      {entryPoint.passcode && (
        <div>
          <Trans id="conference-entry-point-passcode">Passcode:</Trans>
          {entryPoint.passcode}
        </div>
      )}
      {entryPoint.password && (
        <div>
          <Trans id="conference-entry-point-password">Password:</Trans>
          {entryPoint.password}
        </div>
      )}
    </div>
  );
}

function renderAnchor(entryPoint: ConferenceEntryPointModel) {
  return (
    <LinkTooltips>
      <LinkTrap>
        <StyledAnchor href={entryPoint.uri} target="_blank" rel="noopener noreferrer">
          {renderAnchorLabel(entryPoint)}
        </StyledAnchor>
      </LinkTrap>
    </LinkTooltips>
  );
}

function renderAnchorLabel(entryPoint: ConferenceEntryPointModel) {
  if (entryPoint.label) {
    return entryPoint.label;
  }

  // Avoid displaying long video join URLs by rendering fixed text for these entry points.
  if (entryPoint.type === ConferenceEntryPointTypesEnum.VIDEO) {
    return <Trans id="conference-token-join-meeting">Join meeting</Trans>;
  }

  return entryPoint.uri;
}

function renderButtons(
  onRemoveClick: Handler | undefined,
  onCopyClick: Handler | undefined,
  shouldShowCopyCheckmark: boolean,
) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledButtonSectionDiv>
          {onCopyClick && renderCopyButton(onCopyClick, shouldShowCopyCheckmark)}
          {onRemoveClick && (
            <StyledIconButton
              iconName="closeSmall"
              onClick={onRemoveClick}
              label={<Trans id="conference-token-remove">Remove</Trans>}
              color={theme.greys.shade70}
              hoverColor={theme.greys.shade80}
            />
          )}
        </StyledButtonSectionDiv>
      )}
    </UserThemeConsumer>
  );
}

function renderCopyButton(onCopyClick: Handler, shouldShowCopyCheckmark: boolean) {
  if (shouldShowCopyCheckmark) {
    return (
      <UserThemeConsumer>
        {(theme) => <StyledCheckmarkIcon name="checkmarkSelectedSmall" color={theme.greys.shade70} />}
      </UserThemeConsumer>
    );
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledIconButton
          iconName="linkSmall"
          onClick={onCopyClick}
          label={<Trans id="conference-token-copy">Copy</Trans>}
          color={theme.greys.shade70}
          hoverColor={theme.greys.shade80}
        />
      )}
    </UserThemeConsumer>
  );
}
